import { useEffect, useState, useRef, useCallback } from 'react';
import { Card, Tabs, Form, InputNumber, Button, message, Space, Modal, Alert, Select, Collapse, Row, Col } from 'antd';
import { SaveOutlined, EditOutlined } from '@ant-design/icons';
import {
  getPlatformSplitConfig,
  updatePlatformSplitConfig,
  getHouseSplitConfigList,
  updateHouseSplitConfig,
  getUsers,
  getAdminUserById,
} from '../../api';
import PageTitle from '../../components/PageTitle';
import ListToolbar from '../../components/ListToolbar';
import ResizableTable from '../../components/ResizableTable';
import { formatAdminUserSelectLabel } from '../../utils/userDisplay';

const { TabPane } = Tabs;

/**
 * 左侧 A/B/C 占「整单」百分比（与租房服务费比例同单位，如 5 表示 5%）→ 接口存的服务费池内份额 0–1。
 * p3、p4 按各自占订单比例换算；p5 用「ABC 占池合计 − p3 − p4」计算，避免浮点导致 p3+p4+p5 与 (A+B+C)/服务费 不一致。
 * 公司占池比例恒为 1 − (p3+p4+p5)。
 */
function rentalOrderPctToServiceFeeShares(feePct, orderA, orderB, orderC) {
  const fee = Number(feePct) || 0;
  const oa = Number(orderA) || 0;
  const ob = Number(orderB) || 0;
  const oc = Number(orderC) || 0;
  if (!(fee > 0)) return { p3: 0, p4: 0, p5: 0 };
  const sumP = (oa + ob + oc) / fee;
  const p3 = oa / fee;
  const p4 = ob / fee;
  const p5 = sumP - p3 - p4;
  return { p3, p4, p5 };
}

/** 向下截断到指定位小数（展示用）：A/B/C 略少显示，尾差体现在「公司」展示上，避免四舍五入让公司在展示上吃亏 */
function truncateDecimals(value, places) {
  const n = Number(value) || 0;
  const f = 10 ** places;
  return Math.floor(n * f + 1e-9) / f;
}

/** 用户 ID 与下拉 value 统一为正整数，避免接口 string/number 混用导致 Select 已保存值无法回显 */
function normalizeUserSelectId(value) {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * 电/水表等在折叠 Tab 里未挂载时，表单拿不到字段会为 undefined；JSON 序列化会丢掉这些键。
 * 用列表接口上次带回的 record 补全，避免后端收到「半份」更新把费率清掉或忽略售后等字段。
 */
function coalesceHouseFormField(values, record, key, defaultVal = 0) {
  const v = values[key];
  if (v !== undefined && v !== null) return v;
  if (record && record[key] !== undefined && record[key] !== null) return record[key];
  return defaultVal;
}

const SplitConfig = () => {
  const [activeTab, setActiveTab] = useState('platform');
  const [platformForm] = Form.useForm();
  const [houseForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [houseLoading, setHouseLoading] = useState(false);
  const [houseData, setHouseData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [editingHouse, setEditingHouse] = useState(null);
  const [isHouseModalVisible, setIsHouseModalVisible] = useState(false);
  const [platformServiceFeeRate, setPlatformServiceFeeRate] = useState(0);
  const [platformElectricServiceFeeRate, setPlatformElectricServiceFeeRate] = useState(0);
  const [platformWaterServiceFeeRate, setPlatformWaterServiceFeeRate] = useState(0);
  const [platformBankFeeRate, setPlatformBankFeeRate] = useState(0);
  const [platformElectricServiceFeeFixed, setPlatformElectricServiceFeeFixed] = useState(0);
  const [platformWaterServiceFeeFixed, setPlatformWaterServiceFeeFixed] = useState(0);
  const [displayColumns, setDisplayColumns] = useState([]);
  const [userList, setUserList] = useState([]); // 已加载用户（仅当前配置 + 搜索命中），用于标签展示
  const [userRealNameMap, setUserRealNameMap] = useState({}); // 用户ID到真实名字的映射
  const [splitUserSearchHits, setSplitUserSearchHits] = useState([]); // 最近一次关键词搜索返回的用户
  const [splitUserSearchKeyword, setSplitUserSearchKeyword] = useState('');
  const splitUserSearchTimerRef = useRef(null);

  const watchedProfitP3 = Form.useWatch('profit_share_p3_id', houseForm);
  const watchedProfitP4 = Form.useWatch('profit_share_p4_id', houseForm);
  const watchedProfitP5 = Form.useWatch('profit_share_p5_id', houseForm);
  const watchedCollector = Form.useWatch('collector_user_id', houseForm);

  /** 打开弹窗时：不预拉全表，仅按已配置分账/收账/售后用户 ID 拉详情（输入关键词后再搜候选） */
  const loadUsersForSplitModal = useCallback(async (record) => {
    const idSet = new Set(
      [
        normalizeUserSelectId(record.profit_share_p3_id),
        normalizeUserSelectId(record.profit_share_p4_id),
        normalizeUserSelectId(record.profit_share_p5_id),
        normalizeUserSelectId(record.collector_user_id),
        normalizeUserSelectId(record.after_sales_salesman_user_id ?? record.afterSalesSalesmanUserId),
      ].filter(Boolean),
    );
    const list = [];
    for (const uid of idSet) {
      try {
        const r = await getAdminUserById(uid);
        if (r?.success && r.data) list.push(r.data);
      } catch (err) {
        console.warn('加载分账相关用户失败', uid, err);
      }
    }
    setUserList(list);
  }, []);

  /** 分账人/收账人下拉：仅有关键词时才请求接口；结果写入 userList 供展示 */
  const searchSplitUsers = useCallback((keyword) => {
    const kw = String(keyword ?? '').trim();
    setSplitUserSearchKeyword(keyword ?? '');
    clearTimeout(splitUserSearchTimerRef.current);
    if (!kw) {
      setSplitUserSearchHits([]);
      return;
    }
    splitUserSearchTimerRef.current = setTimeout(async () => {
      try {
        const res = await getUsers({ page: 1, page_size: 100, keyword: kw });
        const incoming = res?.success && Array.isArray(res.data) ? res.data : [];
        setSplitUserSearchHits(incoming);
        setUserList((prev) => {
          const m = new Map();
          const put = (u) => {
            const id = normalizeUserSelectId(u.id);
            if (id) m.set(id, u);
          };
          prev.forEach(put);
          incoming.forEach(put);
          return Array.from(m.values());
        });
      } catch (err) {
        console.error('搜索用户失败', err);
      }
    }, 300);
  }, []);

  const splitUserSearchHasKeyword = Boolean(String(splitUserSearchKeyword ?? '').trim());

  const splitSelectOptionIds = (fieldValue) => {
    const ids = new Set();
    const cur = normalizeUserSelectId(fieldValue);
    if (cur) ids.add(cur);
    if (splitUserSearchHasKeyword) {
      for (const u of splitUserSearchHits) {
        const id = normalizeUserSelectId(u.id);
        if (id) ids.add(id);
      }
    }
    return Array.from(ids);
  };

  const splitUserSelectNotFound =
    splitUserSearchHasKeyword && splitUserSearchHits.length === 0 ? '未找到匹配用户' : '请输入姓名、昵称或手机进行搜索';

  const clearSplitUserSearchUi = useCallback(() => {
    setSplitUserSearchKeyword('');
    setSplitUserSearchHits([]);
  }, []);

  useEffect(() => () => clearTimeout(splitUserSearchTimerRef.current), []);

  const buildUserDisplayName = (userId) => {
    const normalizedId = normalizeUserSelectId(userId);
    if (!normalizedId) return '';
    const user = userList.find((u) => normalizeUserSelectId(u.id) === normalizedId);
    if (user) return formatAdminUserSelectLabel(user);
    const rn =
      userRealNameMap[normalizedId] ??
      userRealNameMap[String(normalizedId)] ??
      '';
    if (rn) return `${rn}（ID: ${normalizedId}）`;
    return `用户ID ${normalizedId}（未加载到列表）`;
  };

  // 加载平台分账配置
  const loadPlatformConfig = async () => {
    setLoading(true);
    try {
      const res = await getPlatformSplitConfig();
      console.log('加载平台配置响应：', res);
      if (res?.success && res.data) {
        console.log('后端返回的原始数据：', res.data);
        const rentRate = parseFloat(res.data.service_fee_rate || 0);
        const electricRate = parseFloat(res.data.electric_service_fee_rate || 0);
        const waterRate = parseFloat(res.data.water_service_fee_rate || 0);
        const bankRate = parseFloat(res.data.bank_fee_rate || 0);
        const electricFixed = parseFloat(res.data.electric_service_fee_fixed || 0);
        const waterFixed = parseFloat(res.data.water_service_fee_fixed || 0);
        console.log('解析后的值：', { rentRate, electricRate, waterRate, bankRate, electricFixed, waterFixed });
        setPlatformServiceFeeRate(rentRate);
        setPlatformElectricServiceFeeRate(electricRate);
        setPlatformWaterServiceFeeRate(waterRate);
        setPlatformBankFeeRate(bankRate);
        setPlatformElectricServiceFeeFixed(electricFixed);
        setPlatformWaterServiceFeeFixed(waterFixed);
        // 使用 setTimeout 确保表单更新
        setTimeout(() => {
          platformForm.setFieldsValue({
            service_fee_rate: rentRate,
            electric_service_fee_rate: electricRate,
            water_service_fee_rate: waterRate,
            bank_fee_rate: bankRate, // 后端存储的是百分数，直接显示
            electric_service_fee_fixed: electricFixed,
            water_service_fee_fixed: waterFixed,
          });
          // 强制触发表单字段更新
          platformForm.validateFields();
        }, 0);
      }
    } catch (err) {
      console.error('加载平台配置失败：', err);
      message.error('加载平台配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 保存平台分账配置
  const handleSavePlatformConfig = async () => {
    try {
      const values = await platformForm.validateFields();
      setLoading(true);
      const res = await updatePlatformSplitConfig({
        service_fee_rate: values.service_fee_rate.toFixed(2),
        electric_service_fee_rate: values.electric_service_fee_rate.toFixed(2),
        water_service_fee_rate: values.water_service_fee_rate.toFixed(2),
        bank_fee_rate: values.bank_fee_rate.toFixed(2), // 后端存储百分数，直接保存（与其他手续费一致）
        electric_service_fee_fixed: (values.electric_service_fee_fixed ?? 0).toFixed(3),
        water_service_fee_fixed: (values.water_service_fee_fixed ?? 0).toFixed(3),
      });
      console.log('保存平台配置响应：', res);
      if (res?.success) {
        message.success(`平台分账配置保存成功！租房=${values.service_fee_rate}%, 电表=${values.electric_service_fee_rate}%, 水表=${values.water_service_fee_rate}%, 银行手续费=${values.bank_fee_rate}%, 电表定额=${values.electric_service_fee_fixed ?? 0}元/度, 水表定额=${values.water_service_fee_fixed ?? 0}元/吨`);
        // 如果后端返回了更新后的数据，直接使用；否则重新加载
        if (res.data) {
          console.log('保存后返回的数据：', res.data);
          const rentRate = parseFloat(res.data.service_fee_rate || values.service_fee_rate || 0);
          const electricRate = parseFloat(res.data.electric_service_fee_rate || values.electric_service_fee_rate || 0);
          const waterRate = parseFloat(res.data.water_service_fee_rate || values.water_service_fee_rate || 0);
          const bankRate = parseFloat(res.data.bank_fee_rate || values.bank_fee_rate || 0);
          const electricFixed = parseFloat(res.data.electric_service_fee_fixed ?? values.electric_service_fee_fixed ?? 0);
          const waterFixed = parseFloat(res.data.water_service_fee_fixed ?? values.water_service_fee_fixed ?? 0);
          setPlatformServiceFeeRate(rentRate);
          setPlatformElectricServiceFeeRate(electricRate);
          setPlatformWaterServiceFeeRate(waterRate);
          setPlatformBankFeeRate(bankRate);
          setPlatformElectricServiceFeeFixed(electricFixed);
          setPlatformWaterServiceFeeFixed(waterFixed);
          platformForm.setFieldsValue({
            service_fee_rate: rentRate,
            electric_service_fee_rate: electricRate,
            water_service_fee_rate: waterRate,
            bank_fee_rate: bankRate,
            electric_service_fee_fixed: electricFixed,
            water_service_fee_fixed: waterFixed,
          });
        } else {
          // 重新加载配置，确保显示最新值
          await loadPlatformConfig();
        }
      } else {
        message.error(res?.error || res?.message || '保存平台配置失败');
      }
    } catch (err) {
      console.error('保存平台配置失败：', err);
      if (err.errorFields) {
        message.error('请检查输入的数据');
      } else {
        const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || '保存平台配置失败';
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载房源分账配置列表
  const loadHouseConfigList = async (search = '', page = 1, pageSize = 20) => {
    setHouseLoading(true);
    try {
      const res = await getHouseSplitConfigList(search, page, pageSize);
      if (res?.success && res.data) {
        setHouseData(res.data.list || []);
        setPagination({
          current: res.data.page || page,
          pageSize: res.data.page_size || pageSize,
          total: res.data.total || 0,
        });
        // 保存用户真实名字映射（用于下拉框显示）
        if (res.data.user_real_names) {
          setUserRealNameMap(res.data.user_real_names);
        }
      }
    } catch (err) {
      console.error('加载房源配置列表失败：', err);
      message.error('加载房源配置列表失败');
    } finally {
      setHouseLoading(false);
    }
  };

  // 打开编辑房源配置弹窗
  const handleEditHouse = async (record) => {
    setEditingHouse(record);
    // 如果实际值为0.00，编辑时也显示平台配置值，但保存时如果用户没改就保存0.00
    const displayServiceFeeRate = record.service_fee_rate_display !== undefined 
      ? record.service_fee_rate_display 
      : (record.service_fee_rate || 0);
    const displayElectricServiceFeeRate = record.electric_service_fee_rate_display !== undefined 
      ? record.electric_service_fee_rate_display 
      : (record.electric_service_fee_rate || 0);
    const displayWaterServiceFeeRate = record.water_service_fee_rate_display !== undefined 
      ? record.water_service_fee_rate_display 
      : (record.water_service_fee_rate || 0);
    // 获取银行手续费比例的显示值
    const displayRentBankFeeRate = record.service_fee_rate_p6_display !== undefined 
      ? record.service_fee_rate_p6_display 
      : (record.service_fee_rate_p6 || 0);
    const displayElectricBankFeeRate = record.electric_service_fee_rate_p6_display !== undefined 
      ? record.electric_service_fee_rate_p6_display 
      : (record.electric_service_fee_rate_p6 || 0);
    const displayWaterBankFeeRate = record.water_service_fee_rate_p6_display !== undefined 
      ? record.water_service_fee_rate_p6_display 
      : (record.water_service_fee_rate_p6 || 0);
    const displayElectricServiceFeeFixed = record.electric_service_fee_fixed_display !== undefined
      ? record.electric_service_fee_fixed_display
      : (record.electric_service_fee_fixed ?? 0);
    const displayWaterServiceFeeFixed = record.water_service_fee_fixed_display !== undefined
      ? record.water_service_fee_fixed_display
      : (record.water_service_fee_fixed ?? 0);
    
    const rentFeePct = displayServiceFeeRate || 0;
    const rentFeeDec = rentFeePct / 100;
    const rentP3 = record.service_fee_rate_p3 || 0;
    const rentP4 = record.service_fee_rate_p4 || 0;
    const rentP5 = record.service_fee_rate_p5 || 0;

    houseForm.setFieldsValue({
      service_fee_rate: displayServiceFeeRate, // 显示时用显示值
      electric_service_fee_rate: displayElectricServiceFeeRate,
      water_service_fee_rate: displayWaterServiceFeeRate,
      // 租房服务费分账比例（接口存「占服务费」份额 0–1）
      service_fee_rate_p3: rentP3,
      service_fee_rate_p4: rentP4,
      service_fee_rate_p5: rentP5,
      // 左侧可编辑：占订单金额的百分比（与上方租房服务费比例同单位，如 5 表示 5%）
      rent_order_pct_a: rentFeeDec > 0 ? rentP3 * rentFeeDec * 100 : 0,
      rent_order_pct_b: rentFeeDec > 0 ? rentP4 * rentFeeDec * 100 : 0,
      rent_order_pct_c: rentFeeDec > 0 ? rentP5 * rentFeeDec * 100 : 0,
      service_fee_rate_p6: 0, // 房租无银行手续费
      // 电表服务费分账比例
      electric_service_fee_rate_p3: record.electric_service_fee_rate_p3 || 0,
      electric_service_fee_rate_p4: record.electric_service_fee_rate_p4 || 0,
      electric_service_fee_rate_p5: record.electric_service_fee_rate_p5 || 0,
      electric_service_fee_rate_p6: displayElectricBankFeeRate,
      // 水表服务费分账比例
      water_service_fee_rate_p3: record.water_service_fee_rate_p3 || 0,
      water_service_fee_rate_p4: record.water_service_fee_rate_p4 || 0,
      water_service_fee_rate_p5: record.water_service_fee_rate_p5 || 0,
      water_service_fee_rate_p6: displayWaterBankFeeRate,
      // 电表/水表服务费模式与定额
      electric_service_fee_mode: record.electric_service_fee_mode ?? 1,
      water_service_fee_mode: record.water_service_fee_mode ?? 1,
      electric_service_fee_fixed: displayElectricServiceFeeFixed,
      water_service_fee_fixed: displayWaterServiceFeeFixed,
      // 电表定额服务费分账比例
      electric_service_fee_fixed_p3: record.electric_service_fee_fixed_p3 ?? 0,
      electric_service_fee_fixed_p4: record.electric_service_fee_fixed_p4 ?? 0,
      electric_service_fee_fixed_p5: record.electric_service_fee_fixed_p5 ?? 0,
      electric_service_fee_fixed_p6: record.electric_service_fee_fixed_p6 ?? 0,
      // 水表定额服务费分账比例
      water_service_fee_fixed_p3: record.water_service_fee_fixed_p3 ?? 0,
      water_service_fee_fixed_p4: record.water_service_fee_fixed_p4 ?? 0,
      water_service_fee_fixed_p5: record.water_service_fee_fixed_p5 ?? 0,
      water_service_fee_fixed_p6: record.water_service_fee_fixed_p6 ?? 0,
      // 分账人用户ID（与下拉选项 value 类型一致，保证回显）
      profit_share_p3_id: normalizeUserSelectId(record.profit_share_p3_id),
      profit_share_p4_id: normalizeUserSelectId(record.profit_share_p4_id),
      profit_share_p5_id: normalizeUserSelectId(record.profit_share_p5_id),
      // 售后联系人用户ID（可空，仅允许 A/B/C；兼容驼峰字段名）
      after_sales_salesman_user_id: normalizeUserSelectId(
        record.after_sales_salesman_user_id ?? record.afterSalesSalesmanUserId,
      ),
      // 房源指定收账人ID
      collector_user_id: normalizeUserSelectId(record.collector_user_id),
    });
    clearSplitUserSearchUi();
    await loadUsersForSplitModal(record);
    setIsHouseModalVisible(true);
  };

  // 保存房源分账配置
  const handleSaveHouseConfig = async () => {
    try {
      const values = await houseForm.validateFields();
      // 按模式验证：电表/水表只验证当前选中模式对应的分账比例
      const electricMode = values.electric_service_fee_mode ?? 1;
      const waterMode = values.water_service_fee_mode ?? 1;
      if (electricMode === 1) {
        const electricAbcTotal = (values.electric_service_fee_rate_p3 || 0) + (values.electric_service_fee_rate_p4 || 0) + (values.electric_service_fee_rate_p5 || 0);
        if (electricAbcTotal > 1) {
          message.error('电表分账人A、B、C比例总和不能超过1');
          return;
        }
      } else {
        const electricFixedAbcTotal = (values.electric_service_fee_fixed_p3 || 0) + (values.electric_service_fee_fixed_p4 || 0) + (values.electric_service_fee_fixed_p5 || 0);
        if (electricFixedAbcTotal > 1) {
          message.error('电表定额分账人A、B、C比例总和不能超过1');
          return;
        }
      }
      if (waterMode === 1) {
        const waterAbcTotal = (values.water_service_fee_rate_p3 || 0) + (values.water_service_fee_rate_p4 || 0) + (values.water_service_fee_rate_p5 || 0);
        if (waterAbcTotal > 1) {
          message.error('水表分账人A、B、C比例总和不能超过1');
          return;
        }
      } else {
        const waterFixedAbcTotal = (values.water_service_fee_fixed_p3 || 0) + (values.water_service_fee_fixed_p4 || 0) + (values.water_service_fee_fixed_p5 || 0);
        if (waterFixedAbcTotal > 1) {
          message.error('水表定额分账人A、B、C比例总和不能超过1');
          return;
        }
      }
      // 租房分账：左侧为占订单 %，换算后 p3+p4+p5 为占服务费份额
      const rentFeePct = Number(values.service_fee_rate) || 0;
      const roa = Number(values.rent_order_pct_a) || 0;
      const rob = Number(values.rent_order_pct_b) || 0;
      const roc = Number(values.rent_order_pct_c) || 0;
      if (roa + rob + roc > rentFeePct + 1e-9) {
        message.error('租房 A/B/C 占订单百分比之和不能超过「租房服务费比例」');
        return;
      }
      const { p3: rentP3, p4: rentP4, p5: rentP5 } = rentalOrderPctToServiceFeeShares(
        rentFeePct,
        roa,
        rob,
        roc,
      );
      const rentAbcTotal = rentP3 + rentP4 + rentP5;
      if (rentAbcTotal > 1 + 1e-9) {
        message.error('租房分账人A、B、C占服务费比例总和不能超过100%');
        return;
      }
      setLoading(true);
      // 处理分账人ID：如果为空或0，传 null；否则传数字
      const profitShareP3ID = normalizeUserSelectId(values.profit_share_p3_id) ?? null;
      const profitShareP4ID = normalizeUserSelectId(values.profit_share_p4_id) ?? null;
      const profitShareP5ID = normalizeUserSelectId(values.profit_share_p5_id) ?? null;
      // 售后联系人：可空；如有值必须为当前 A/B/C 之一
      const afterSalesSalesmanUserID = normalizeUserSelectId(values.after_sales_salesman_user_id) ?? null;
      if (afterSalesSalesmanUserID !== null) {
        const abcIDs = [profitShareP3ID, profitShareP4ID, profitShareP5ID].filter(v => v && v > 0);
        if (!abcIDs.includes(afterSalesSalesmanUserID)) {
          message.error('售后联系人只能选择当前分账人 A/B/C 之一');
          return;
        }
      }
      // 处理收账人ID：如果为空或0，传 null；否则传数字
      const collectorUserID = normalizeUserSelectId(values.collector_user_id) ?? null;

      const EH = editingHouse || {};
      const res = await updateHouseSplitConfig(editingHouse.id, {
        service_fee_rate: coalesceHouseFormField(values, EH, 'service_fee_rate', 0),
        electric_service_fee_rate: coalesceHouseFormField(values, EH, 'electric_service_fee_rate', 0),
        water_service_fee_rate: coalesceHouseFormField(values, EH, 'water_service_fee_rate', 0),
        // 租房服务费分账比例（房租无银行手续费，固定传0）
        service_fee_rate_p3: rentP3,
        service_fee_rate_p4: rentP4,
        service_fee_rate_p5: rentP5,
        service_fee_rate_p6: 0,
        // 电表服务费分账比例
        electric_service_fee_rate_p3: coalesceHouseFormField(values, EH, 'electric_service_fee_rate_p3', 0),
        electric_service_fee_rate_p4: coalesceHouseFormField(values, EH, 'electric_service_fee_rate_p4', 0),
        electric_service_fee_rate_p5: coalesceHouseFormField(values, EH, 'electric_service_fee_rate_p5', 0),
        electric_service_fee_rate_p6: coalesceHouseFormField(values, EH, 'electric_service_fee_rate_p6', 0),
        // 电表/水表服务费模式与定额
        electric_service_fee_mode: coalesceHouseFormField(values, EH, 'electric_service_fee_mode', 1),
        water_service_fee_mode: coalesceHouseFormField(values, EH, 'water_service_fee_mode', 1),
        electric_service_fee_fixed: coalesceHouseFormField(values, EH, 'electric_service_fee_fixed', 0),
        water_service_fee_fixed: coalesceHouseFormField(values, EH, 'water_service_fee_fixed', 0),
        // 电表定额服务费分账比例
        electric_service_fee_fixed_p3: coalesceHouseFormField(values, EH, 'electric_service_fee_fixed_p3', 0),
        electric_service_fee_fixed_p4: coalesceHouseFormField(values, EH, 'electric_service_fee_fixed_p4', 0),
        electric_service_fee_fixed_p5: coalesceHouseFormField(values, EH, 'electric_service_fee_fixed_p5', 0),
        electric_service_fee_fixed_p6: coalesceHouseFormField(values, EH, 'electric_service_fee_fixed_p6', 0),
        // 水表定额服务费分账比例
        water_service_fee_fixed_p3: coalesceHouseFormField(values, EH, 'water_service_fee_fixed_p3', 0),
        water_service_fee_fixed_p4: coalesceHouseFormField(values, EH, 'water_service_fee_fixed_p4', 0),
        water_service_fee_fixed_p5: coalesceHouseFormField(values, EH, 'water_service_fee_fixed_p5', 0),
        water_service_fee_fixed_p6: coalesceHouseFormField(values, EH, 'water_service_fee_fixed_p6', 0),
        // 水表服务费分账比例
        water_service_fee_rate_p3: coalesceHouseFormField(values, EH, 'water_service_fee_rate_p3', 0),
        water_service_fee_rate_p4: coalesceHouseFormField(values, EH, 'water_service_fee_rate_p4', 0),
        water_service_fee_rate_p5: coalesceHouseFormField(values, EH, 'water_service_fee_rate_p5', 0),
        water_service_fee_rate_p6: coalesceHouseFormField(values, EH, 'water_service_fee_rate_p6', 0),
        // 分账人用户ID
        profit_share_p3_id: profitShareP3ID,
        profit_share_p4_id: profitShareP4ID,
        profit_share_p5_id: profitShareP5ID,
        // 售后联系人用户ID（可空，仅允许 A/B/C）
        after_sales_salesman_user_id: afterSalesSalesmanUserID,
        // 房源指定收账人ID
        collector_user_id: collectorUserID,
      });
      console.log('保存房源配置响应：', res);
      if (res?.success) {
        const houseName = editingHouse?.house_name || '房源';
        message.success(`${houseName}的分账配置保存成功！`);
        clearTimeout(splitUserSearchTimerRef.current);
        setIsHouseModalVisible(false);
        setEditingHouse(null);
        clearSplitUserSearchUi();
        setUserList([]);
        houseForm.resetFields();
        // 仅展示接口 GET 列表返回的数据，不在前端伪造字段
        await loadHouseConfigList(searchText, pagination.current, pagination.pageSize);
      } else {
        message.error(res?.error || res?.message || '保存房源配置失败');
      }
    } catch (err) {
      console.error('保存房源配置失败：', err);
      if (err.errorFields) {
        message.error('请检查输入的数据');
      } else {
        const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || '保存房源配置失败';
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // 搜索房源
  const handleSearch = () => {
    loadHouseConfigList(searchText, 1, pagination.pageSize);
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    loadHouseConfigList('', 1, pagination.pageSize);
  };

  // 刷新数据
  const handleRefresh = () => {
    if (activeTab === 'platform') {
      loadPlatformConfig();
    } else {
      loadHouseConfigList(searchText, pagination.current, pagination.pageSize);
    }
  };

  // 分页变化
  const handleTableChange = (newPagination) => {
    loadHouseConfigList(searchText, newPagination.current, newPagination.pageSize);
  };

  // 切换标签页
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'platform') {
      loadPlatformConfig();
    } else {
      loadHouseConfigList();
    }
  };

  useEffect(() => {
    // 用户量较大时全量 getUsers() 会超时；此页回显优先依赖 split-config/houses 返回的 user_real_names
    if (activeTab === 'platform') {
      loadPlatformConfig();
    } else {
      // 加载房源列表前，先加载平台配置（用于显示）
      loadPlatformConfig().then(() => {
        loadHouseConfigList();
      }).catch(() => {
        loadHouseConfigList();
      });
    }
  }, [activeTab]);

  // 原始房源配置表格列
  const originalHouseColumns = [
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditHouse(record)}
        >
          编辑
        </Button>
      ),
    },
    {
      title: '房东ID',
      dataIndex: 'fangdong_user_id',
      key: 'fangdong_user_id',
      width: 100,
      render: (value) => value || '-',
    },
    {
      title: '房源指定收账人ID',
      dataIndex: 'collector_user_id',
      key: 'collector_user_id',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: '售后联系人ID',
      dataIndex: 'after_sales_salesman_user_id',
      key: 'after_sales_salesman_user_id',
      width: 130,
      render: (_, record) =>
        record.after_sales_salesman_user_id ?? record.afterSalesSalesmanUserId ?? '-',
    },
    {
      title: '房源ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '房源名称',
      dataIndex: 'house_name',
      key: 'house_name',
      width: 200,
    },
    {
      title: '地址',
      dataIndex: 'address',
      key: 'address',
      width: 250,
    },
    {
      title: '房东房租分账比例',
      key: 'landlord_rent_split_rate',
      width: 140,
      render: (_, record) => {
        const serviceFeeValue = record.service_fee_rate_display !== undefined 
          ? record.service_fee_rate_display 
          : (record.service_fee_rate || 0);
        const landlordRate = Math.max(0, 100 - serviceFeeValue);
        return `${landlordRate.toFixed(2)}%`;
      },
    },
    {
      title: '房东电费分账比例',
      key: 'landlord_electric_split_rate',
      width: 140,
      render: (_, record) => {
        const serviceFeeValue = record.electric_service_fee_rate_display !== undefined 
          ? record.electric_service_fee_rate_display 
          : (record.electric_service_fee_rate || 0);
        const landlordRate = Math.max(0, 100 - serviceFeeValue);
        return `${landlordRate.toFixed(2)}%`;
      },
    },
    {
      title: '房东水费分账比例',
      key: 'landlord_water_split_rate',
      width: 140,
      render: (_, record) => {
        const serviceFeeValue = record.water_service_fee_rate_display !== undefined 
          ? record.water_service_fee_rate_display 
          : (record.water_service_fee_rate || 0);
        const landlordRate = Math.max(0, 100 - serviceFeeValue);
        return `${landlordRate.toFixed(2)}%`;
      },
    },
    {
      title: '租房服务费比例',
      dataIndex: 'service_fee_rate',
      key: 'service_fee_rate',
      width: 130,
      render: (value, record) => {
        // 如果实际值为0.00，显示平台配置值（service_fee_rate_display）
        const displayValue = record.service_fee_rate_display !== undefined 
          ? record.service_fee_rate_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {displayValue.toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    {
      title: '电表服务费费率',
      dataIndex: 'electric_service_fee_rate',
      key: 'electric_service_fee_rate',
      width: 130,
      render: (value, record) => {
        // 公式分账模式下，展示“公式分账 + 单价”，更直观
        if (record.electric_service_fee_mode === 2) {
          const fixedValue =
            record.electric_service_fee_fixed_display !== undefined
              ? record.electric_service_fee_fixed_display
              : (record.electric_service_fee_fixed ?? 0);
          const isUsingPlatformFixed = (record.electric_service_fee_fixed ?? 0) === 0;
          return (
            <span>
              {fixedValue.toFixed(3)} 元/度
              {isUsingPlatformFixed && (
                <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>
              )}
            </span>
          );
        }
        // 如果实际值为0.00，显示平台配置值
        const displayValue = record.electric_service_fee_rate_display !== undefined 
          ? record.electric_service_fee_rate_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {displayValue.toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    {
      title: '水表服务费费率',
      dataIndex: 'water_service_fee_rate',
      key: 'water_service_fee_rate',
      width: 130,
      render: (value, record) => {
        // 公式分账模式下，展示“公式分账 + 单价”，更直观
        if (record.water_service_fee_mode === 2) {
          const fixedValue =
            record.water_service_fee_fixed_display !== undefined
              ? record.water_service_fee_fixed_display
              : (record.water_service_fee_fixed ?? 0);
          const isUsingPlatformFixed = (record.water_service_fee_fixed ?? 0) === 0;
          return (
            <span>
              {fixedValue.toFixed(3)} 元/吨
              {isUsingPlatformFixed && (
                <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>
              )}
            </span>
          );
        }
        // 如果实际值为0.00，显示平台配置值
        const displayValue = record.water_service_fee_rate_display !== undefined 
          ? record.water_service_fee_rate_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {displayValue.toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    {
      title: '电表服务费分账模式',
      dataIndex: 'electric_service_fee_mode',
      key: 'electric_service_fee_mode',
      width: 140,
      render: (value) => (value === 2 ? '公式分账' : '百分比分账'),
    },
    {
      title: '水表服务费分账模式',
      dataIndex: 'water_service_fee_mode',
      key: 'water_service_fee_mode',
      width: 140,
      render: (value) => (value === 2 ? '公式分账' : '百分比分账'),
    },
    {
      title: '电表每度服务费（公式分账）',
      dataIndex: 'electric_service_fee_fixed',
      key: 'electric_service_fee_fixed',
      width: 180,
      render: (value, record) => {
        const displayValue = record.electric_service_fee_fixed_display !== undefined
          ? record.electric_service_fee_fixed_display
          : (value ?? 0);
        const isUsingPlatform = (value ?? 0) === 0;
        return (
          <span>
            {displayValue.toFixed(3)} 元/度
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    {
      title: '水表每吨服务费（公式分账）',
      dataIndex: 'water_service_fee_fixed',
      key: 'water_service_fee_fixed',
      width: 180,
      render: (value, record) => {
        const displayValue = record.water_service_fee_fixed_display !== undefined
          ? record.water_service_fee_fixed_display
          : (value ?? 0);
        const isUsingPlatform = (value ?? 0) === 0;
        return (
          <span>
            {displayValue.toFixed(3)} 元/吨
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    // 租房服务费分账比例
    {
      title: '租房公司比例',
      key: 'rent_company_rate',
      width: 120,
      render: (_, record) => {
        const abcTotal = (record.service_fee_rate_p3 || 0) + (record.service_fee_rate_p4 || 0) + (record.service_fee_rate_p5 || 0);
        const companyRate = Math.max(0, 1 - abcTotal);
        return `${(companyRate * 100).toFixed(2)}%`;
      },
    },
    {
      title: '租房分账人A比例',
      dataIndex: 'service_fee_rate_p3',
      key: 'service_fee_rate_p3',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '租房分账人B比例',
      dataIndex: 'service_fee_rate_p4',
      key: 'service_fee_rate_p4',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '租房分账人C比例',
      dataIndex: 'service_fee_rate_p5',
      key: 'service_fee_rate_p5',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '租房银行手续费比例',
      dataIndex: 'service_fee_rate_p6',
      key: 'service_fee_rate_p6',
      width: 140,
      render: (value, record) => {
        const displayValue = record.service_fee_rate_p6_display !== undefined 
          ? record.service_fee_rate_p6_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {(displayValue * 100).toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    // 电表服务费分账比例
    {
      title: '电表公司比例',
      key: 'electric_company_rate',
      width: 120,
      render: (_, record) => {
        const abcTotal = (record.electric_service_fee_rate_p3 || 0) + (record.electric_service_fee_rate_p4 || 0) + (record.electric_service_fee_rate_p5 || 0);
        const companyRate = Math.max(0, 1 - abcTotal);
        return `${(companyRate * 100).toFixed(2)}%`;
      },
    },
    {
      title: '电表分账人A比例',
      dataIndex: 'electric_service_fee_rate_p3',
      key: 'electric_service_fee_rate_p3',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表分账人B比例',
      dataIndex: 'electric_service_fee_rate_p4',
      key: 'electric_service_fee_rate_p4',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表分账人C比例',
      dataIndex: 'electric_service_fee_rate_p5',
      key: 'electric_service_fee_rate_p5',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表银行手续费比例',
      dataIndex: 'electric_service_fee_rate_p6',
      key: 'electric_service_fee_rate_p6',
      width: 140,
      render: (value, record) => {
        const displayValue = record.electric_service_fee_rate_p6_display !== undefined 
          ? record.electric_service_fee_rate_p6_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {(displayValue * 100).toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    // 电表定额服务费分账比例
    {
      title: '电表公式分账人A比例',
      dataIndex: 'electric_service_fee_fixed_p3',
      key: 'electric_service_fee_fixed_p3',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表公式分账人B比例',
      dataIndex: 'electric_service_fee_fixed_p4',
      key: 'electric_service_fee_fixed_p4',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表公式分账人C比例',
      dataIndex: 'electric_service_fee_fixed_p5',
      key: 'electric_service_fee_fixed_p5',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '电表公式银行手续费比例',
      dataIndex: 'electric_service_fee_fixed_p6',
      key: 'electric_service_fee_fixed_p6',
      width: 150,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    // 水表服务费分账比例
    {
      title: '水表公司比例',
      key: 'water_company_rate',
      width: 120,
      render: (_, record) => {
        const abcTotal = (record.water_service_fee_rate_p3 || 0) + (record.water_service_fee_rate_p4 || 0) + (record.water_service_fee_rate_p5 || 0);
        const companyRate = Math.max(0, 1 - abcTotal);
        return `${(companyRate * 100).toFixed(2)}%`;
      },
    },
    {
      title: '水表分账人A比例',
      dataIndex: 'water_service_fee_rate_p3',
      key: 'water_service_fee_rate_p3',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表分账人B比例',
      dataIndex: 'water_service_fee_rate_p4',
      key: 'water_service_fee_rate_p4',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表分账人C比例',
      dataIndex: 'water_service_fee_rate_p5',
      key: 'water_service_fee_rate_p5',
      width: 120,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表银行手续费比例',
      dataIndex: 'water_service_fee_rate_p6',
      key: 'water_service_fee_rate_p6',
      width: 140,
      render: (value, record) => {
        const displayValue = record.water_service_fee_rate_p6_display !== undefined 
          ? record.water_service_fee_rate_p6_display 
          : (value || 0);
        const isUsingPlatform = (value || 0) === 0;
        return (
          <span>
            {(displayValue * 100).toFixed(2)}%
            {isUsingPlatform && <span style={{ color: '#999', fontSize: '12px', marginLeft: '4px' }}>(平台)</span>}
          </span>
        );
      },
    },
    // 水表定额服务费分账比例
    {
      title: '水表公式分账人A比例',
      dataIndex: 'water_service_fee_fixed_p3',
      key: 'water_service_fee_fixed_p3',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表公式分账人B比例',
      dataIndex: 'water_service_fee_fixed_p4',
      key: 'water_service_fee_fixed_p4',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表公式分账人C比例',
      dataIndex: 'water_service_fee_fixed_p5',
      key: 'water_service_fee_fixed_p5',
      width: 130,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '水表公式银行手续费比例',
      dataIndex: 'water_service_fee_fixed_p6',
      key: 'water_service_fee_fixed_p6',
      width: 150,
      render: (value) => `${((value || 0) * 100).toFixed(2)}%`,
    },
    {
      title: '分账人A用户ID',
      dataIndex: 'profit_share_p3_id',
      key: 'profit_share_p3_id',
      width: 130,
      render: (value) => value || '-',
    },
    {
      title: '分账人B用户ID',
      dataIndex: 'profit_share_p4_id',
      key: 'profit_share_p4_id',
      width: 130,
      render: (value) => value || '-',
    },
    {
      title: '分账人C用户ID',
      dataIndex: 'profit_share_p5_id',
      key: 'profit_share_p5_id',
      width: 130,
      render: (value) => value || '-',
    },
  ];

  // 默认展示的列（列表初次进入时更简洁，其余列可在表头设置中打开）
  const defaultVisibleColumnKeys = [
    'action',
    'fangdong_user_id',
    'id',
    'house_name',
    'address',
    'service_fee_rate',
    'electric_service_fee_rate',
    'water_service_fee_rate',
    'electric_service_fee_rate_p6', // 水电表银行手续费（共用，房源为0时用平台默认）
    'profit_share_p3_id',
    'profit_share_p4_id',
    'profit_share_p5_id',
    'collector_user_id',
    'after_sales_salesman_user_id',
  ];

  // 从localStorage加载列设置并应用到表格（只处理隐藏；无历史配置时按默认精简列展示）
  useEffect(() => {
    const storageKey = 'splitConfigHouseList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};

    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }

    const processedColumns = originalHouseColumns
      .filter(col => {
        const state = columnStates[col.key];
        if (state === 'hidden') return false;
        // 首次进入（没有保存过列设置）时，只展示关键列
        if (!saved) {
          return defaultVisibleColumnKeys.includes(col.key);
        }
        return true;
      });

    setDisplayColumns(processedColumns);
  }, []);

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="分账管理" />
      <Card bordered={false} style={{ marginTop: 0 }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="平台分账配置" key="platform">
            <Form
              form={platformForm}
              layout="vertical"
              style={{ maxWidth: 600, marginTop: 20 }}
            >
              {/* 房租：仅百分比分账，无银行手续费 */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginBottom: 12 }}>房租（仅百分比分账）</div>
                <Form.Item
                  label="房租默认手续费（%）"
                  name="service_fee_rate"
                  rules={[
                    { required: true, message: '请输入房租默认手续费' },
                    { type: 'number', min: 0, message: '比例必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="请输入房租默认手续费（例如：4.8）"
                    addonAfter="%"
                  />
                </Form.Item>
              </div>

              {/* 水电表：百分比分账 + 公式分账，共用银行手续费 */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626', marginBottom: 12 }}>水电表（电表 / 水表分开展示，共用银行手续费）</div>

                {/* 银行默认手续费（电表、水表共用） */}
                <Form.Item
                  label="银行默认手续费（%）"
                  name="bank_fee_rate"
                  rules={[
                    { required: true, message: '请输入水电表银行默认手续费' },
                    { type: 'number', min: 0, message: '比例必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="例如：0.6"
                    addonAfter="%"
                  />
                </Form.Item>

                {/* 电表相关 */}
                <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>电表</div>
                <Form.Item
                  label="电表默认服务费（百分比，%）"
                  name="electric_service_fee_rate"
                  rules={[
                    { required: true, message: '请输入电表默认服务费' },
                    { type: 'number', min: 0, message: '比例必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="例如：4.8"
                    addonAfter="%"
                  />
                </Form.Item>
                <Form.Item
                  label="电表每度服务费（公式分账，元/度）"
                  name="electric_service_fee_fixed"
                  rules={[
                    { required: true, message: '请输入平台电表每度服务费' },
                    { type: 'number', min: 0, message: '必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.001}
                    precision={3}
                    placeholder="例如：0.048"
                    addonAfter="元/度"
                  />
                </Form.Item>

                {/* 水表相关 */}
                <div style={{ fontWeight: 600, margin: '12px 0 4px' }}>水表</div>
                <Form.Item
                  label="水表默认服务费（百分比，%）"
                  name="water_service_fee_rate"
                  rules={[
                    { required: true, message: '请输入水表默认服务费' },
                    { type: 'number', min: 0, message: '比例必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.01}
                    precision={2}
                    placeholder="例如：4.8"
                    addonAfter="%"
                  />
                </Form.Item>
                <Form.Item
                  label="水表每吨服务费（公式分账，元/吨）"
                  name="water_service_fee_fixed"
                  rules={[
                    { required: true, message: '请输入平台水表每吨服务费' },
                    { type: 'number', min: 0, message: '必须大于等于0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    step={0.001}
                    precision={3}
                    placeholder="例如：0.048"
                    addonAfter="元/吨"
                  />
                </Form.Item>
              </div>

              <Form.Item>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSavePlatformConfig}
                  loading={loading}
                >
                  保存配置
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="房源分账配置" key="houses">
            <ListToolbar
              searchPlaceholder="搜索房源ID、房源名称或地址"
              searchValue={searchText}
              onSearchChange={setSearchText}
              onSearch={handleSearch}
              onReset={handleReset}
              onRefresh={handleRefresh}
              showBatchDelete={false}
            />

            <ResizableTable
              columns={displayColumns.length > 0 ? displayColumns : originalHouseColumns}
              dataSource={houseData}
              loading={houseLoading}
              rowKey="id"
              storageKey="splitConfigHouseList"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              onChange={handleTableChange}
              scroll={{ x: 1200 }}
            />
          </TabPane>
        </Tabs>

        {/* 编辑房源配置弹窗 */}
        <Modal
          title="编辑房源分账配置"
          open={isHouseModalVisible}
          onCancel={() => {
            clearTimeout(splitUserSearchTimerRef.current);
            setIsHouseModalVisible(false);
            setEditingHouse(null);
            clearSplitUserSearchUi();
            setUserList([]);
            houseForm.resetFields();
          }}
          onOk={handleSaveHouseConfig}
          confirmLoading={loading}
          width={960}
        >
          <Form
            form={houseForm}
            layout="vertical"
            style={{ marginTop: 20 }}
            onValuesChange={(changedValues, allValues) => {
              // 水电表银行手续费共用：任一修改时同步到全部
              const bankFeeKeys = ['electric_service_fee_rate_p6', 'electric_service_fee_fixed_p6', 'water_service_fee_rate_p6', 'water_service_fee_fixed_p6'];
              if (Object.keys(changedValues).some(k => bankFeeKeys.includes(k))) {
                const val = changedValues.electric_service_fee_rate_p6 ?? changedValues.electric_service_fee_fixed_p6
                  ?? changedValues.water_service_fee_rate_p6 ?? changedValues.water_service_fee_fixed_p6
                  ?? allValues.electric_service_fee_rate_p6 ?? allValues.electric_service_fee_fixed_p6
                  ?? allValues.water_service_fee_rate_p6 ?? allValues.water_service_fee_fixed_p6 ?? 0;
                houseForm.setFieldsValue({
                  electric_service_fee_rate_p6: val,
                  electric_service_fee_fixed_p6: val,
                  water_service_fee_rate_p6: val,
                  water_service_fee_fixed_p6: val,
                });
              }
              // 租房：左侧「占订单 %」换算为接口用的「占服务费」比例 p3/p4/p5
              const rentOrderKeys = ['rent_order_pct_a', 'rent_order_pct_b', 'rent_order_pct_c', 'service_fee_rate'];
              if (Object.keys(changedValues).some(k => rentOrderKeys.includes(k))) {
                const fee = Number(allValues.service_fee_rate) || 0;
                const oa = Number(allValues.rent_order_pct_a) || 0;
                const ob = Number(allValues.rent_order_pct_b) || 0;
                const oc = Number(allValues.rent_order_pct_c) || 0;
                const { p3, p4, p5 } = rentalOrderPctToServiceFeeShares(fee, oa, ob, oc);
                houseForm.setFieldsValue({
                  service_fee_rate_p3: p3,
                  service_fee_rate_p4: p4,
                  service_fee_rate_p5: p5,
                });
              }
            }}
          >
            <Alert
              message="服务费费率配置"
              description="请配置租房、电表、水表的服务费费率。房东分账比例 = 100% - 服务费费率。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            {/* 显示房东信息 */}
            {editingHouse && (editingHouse.fangdong_user_id || editingHouse.fangdong_real_name || editingHouse.fangdong_huifu_id) && (
              <Form.Item label="房东信息">
                <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: '4px' }}>
                  <span style={{ marginRight: 16 }}>
                    <strong>ID:</strong> {editingHouse.fangdong_user_id || '-'}
                  </span>
                  <span>
                    <strong>真实名字:</strong> {editingHouse.fangdong_real_name || '-'}
                    {editingHouse.fangdong_huifu_id ? `（huifu_id：${editingHouse.fangdong_huifu_id}）` : ''}
                  </span>
                </div>
              </Form.Item>
            )}

            {/* 显示收账人信息 */}
            {editingHouse && (editingHouse.collector_user_id || editingHouse.collector_real_name || editingHouse.collector_huifu_id) && (
              <Form.Item label="房源指定收账人信息">
                <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: '4px' }}>
                  <span style={{ marginRight: 16 }}>
                    <strong>ID:</strong> {editingHouse.collector_user_id || '-'}
                  </span>
                  <span>
                    <strong>真实名字:</strong> {editingHouse.collector_real_name || '-'}
                    {editingHouse.collector_huifu_id ? `（huifu_id：${editingHouse.collector_huifu_id}）` : ''}
                  </span>
                </div>
              </Form.Item>
            )}

            {/* 房东分账比例：改为可折叠查看，默认收起，避免干扰主要编辑区域 */}
            <Collapse
              size="small"
              bordered={false}
              items={[
                {
                  key: 'landlordRates',
                  label: '房东分账比例（自动计算，可选查看）',
                  children: (
                    <>
                      <Form.Item
                        label="房东房租分账比例（%）"
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues.service_fee_rate !== currentValues.service_fee_rate
                        }
                      >
                        {({ getFieldValue }) => {
                          const serviceFeeRate = getFieldValue('service_fee_rate') || 0;
                          const landlordRate = Math.max(0, 100 - serviceFeeRate);
                          return (
                            <InputNumber
                              style={{ width: '100%' }}
                              value={landlordRate}
                              disabled
                              precision={2}
                              addonAfter="%"
                            />
                          );
                        }}
                      </Form.Item>

                      <Form.Item
                        label="房东电费分账比例（%）"
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues.electric_service_fee_rate !== currentValues.electric_service_fee_rate
                        }
                      >
                        {({ getFieldValue }) => {
                          const electricServiceFeeRate = getFieldValue('electric_service_fee_rate') || 0;
                          const landlordRate = Math.max(0, 100 - electricServiceFeeRate);
                          return (
                            <InputNumber
                              style={{ width: '100%' }}
                              value={landlordRate}
                              disabled
                              precision={2}
                              addonAfter="%"
                            />
                          );
                        }}
                      </Form.Item>

                      <Form.Item
                        label="房东水费分账比例（%）"
                        shouldUpdate={(prevValues, currentValues) =>
                          prevValues.water_service_fee_rate !== currentValues.water_service_fee_rate
                        }
                      >
                        {({ getFieldValue }) => {
                          const waterServiceFeeRate = getFieldValue('water_service_fee_rate') || 0;
                          const landlordRate = Math.max(0, 100 - waterServiceFeeRate);
                          return (
                            <InputNumber
                              style={{ width: '100%' }}
                              value={landlordRate}
                              disabled
                              precision={2}
                              addonAfter="%"
                            />
                          );
                        }}
                      </Form.Item>
                    </>
                  ),
                },
              ]}
              style={{ marginBottom: 8 }}
            />

            <Form.Item
              label="租房服务费比例（%）"
              name="service_fee_rate"
              rules={[
                { required: true, message: '请输入租房服务费比例' },
                { type: 'number', min: 0, message: '比例必须大于等于0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                precision={2}
                placeholder="例如：5.0（0=平台默认）"
                addonAfter="%"
              />
            </Form.Item>

            <Collapse
              size="small"
              bordered={false}
              defaultActiveKey={[]}
              items={[
                {
                  key: 'electric_base',
                  label: '电表设置',
                  children: (
                    <>
                      <Form.Item
                        label="电表服务费费率（%）"
                        name="electric_service_fee_rate"
                        rules={[
                          { required: true, message: '请输入电表服务费费率' },
                          { type: 'number', min: 0, message: '费率必须大于等于0' },
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={0.01}
                          precision={2}
                          placeholder="例如：5.0（0=平台默认）"
                          addonAfter="%"
                        />
                      </Form.Item>

                      <Form.Item
                        label="电表服务费分账模式"
                        name="electric_service_fee_mode"
                        rules={[{ required: true, message: '请选择电表服务费分账模式' }]}
                      >
                        <Select
                          style={{ width: '100%' }}
                          placeholder="请选择"
                          options={[
                            { value: 1, label: '百分比分账' },
                            { value: 2, label: '公式分账' },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item
                        label="电表每度服务费（公式分账，元/度）"
                        name="electric_service_fee_fixed"
                        rules={[
                          { required: true, message: '请输入电表每度服务费' },
                          { type: 'number', min: 0, message: '必须大于等于0' },
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={0.001}
                          precision={3}
                          placeholder="例如：0.048（0=平台默认）"
                          addonAfter="元/度"
                        />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  key: 'water_base',
                  label: '水表设置',
                  children: (
                    <>
                      <Form.Item
                        label="水表服务费费率（%）"
                        name="water_service_fee_rate"
                        rules={[
                          { required: true, message: '请输入水表服务费费率' },
                          { type: 'number', min: 0, message: '费率必须大于等于0' },
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={0.01}
                          precision={2}
                          placeholder="例如：5.0（0=平台默认）"
                          addonAfter="%"
                        />
                      </Form.Item>

                      <Form.Item
                        label="水表服务费分账模式"
                        name="water_service_fee_mode"
                        rules={[{ required: true, message: '请选择水表服务费分账模式' }]}
                      >
                        <Select
                          style={{ width: '100%' }}
                          placeholder="请选择"
                          options={[
                            { value: 1, label: '百分比分账' },
                            { value: 2, label: '公式分账' },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item
                        label="水表每吨服务费（公式分账，元/吨）"
                        name="water_service_fee_fixed"
                        rules={[
                          { required: true, message: '请输入水表每吨服务费' },
                          { type: 'number', min: 0, message: '必须大于等于0' },
                        ]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={0.001}
                          precision={3}
                          placeholder="例如：0.048（0=平台默认）"
                          addonAfter="元/吨"
                        />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
              style={{ marginBottom: 8 }}
            />

            <div style={{ fontSize: 12, color: '#999', marginTop: 4, marginBottom: 12 }}>
              提示：填 0 使用平台默认配置；银行手续费先从订单金额扣除，剩余部分再按公司和分账人 A/B/C 分配。
            </div>

            <Tabs defaultActiveKey="rent" style={{ marginTop: 16 }}>
              <TabPane tab="租房" key="rent">
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="租房服务费分账"
                  description={
                    <span>
                      左侧填写 A/B/C 各自占<strong>整单金额</strong>的百分比（与上方「租房服务费比例」同单位，如 1 表示订单的 1%）；三项之和需不超过该服务费比例；公司占订单的剩余部分由系统自动对应到「占服务费池」里的公司份额。
                      右侧为提交接口使用的<strong>占服务费池内</strong>的分配（只读）：换算逻辑同前；<strong>仅展示</strong>时 A/B/C 按<strong>向下截断</strong>保留小数位，公司行用 100% 减去三项展示值，合计恒为 100%，不把四舍五入的尾差摊给公司。
                    </span>
                  }
                />
                <Form.Item name="service_fee_rate_p3" hidden rules={[{ type: 'number', min: 0, max: 1 }]}>
                  <InputNumber />
                </Form.Item>
                <Form.Item name="service_fee_rate_p4" hidden rules={[{ type: 'number', min: 0, max: 1 }]}>
                  <InputNumber />
                </Form.Item>
                <Form.Item name="service_fee_rate_p5" hidden rules={[{ type: 'number', min: 0, max: 1 }]}>
                  <InputNumber />
                </Form.Item>
                <Row gutter={[24, 16]}>
                  <Col xs={24} lg={24}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>占订单金额（可编辑）</div>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) =>
                        prev.service_fee_rate !== cur.service_fee_rate
                      }
                    >
                      {({ getFieldValue }) => {
                        const feeCap = Number(getFieldValue('service_fee_rate')) || 0;
                        return (
                          <>
                            <Form.Item
                              label="分账人 A（占订单 %）"
                              name="rent_order_pct_a"
                              rules={[
                                { required: true, message: '请输入分账人 A 占订单比例' },
                                { type: 'number', min: 0, message: '不能小于 0' },
                              ]}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={feeCap}
                                step={0.01}
                                precision={4}
                                addonAfter="%"
                                placeholder={`0 ~ ${feeCap || 0}`}
                              />
                            </Form.Item>
                            <Form.Item
                              label="分账人 B（占订单 %）"
                              name="rent_order_pct_b"
                              rules={[
                                { required: true, message: '请输入分账人 B 占订单比例' },
                                { type: 'number', min: 0, message: '不能小于 0' },
                              ]}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={feeCap}
                                step={0.01}
                                precision={4}
                                addonAfter="%"
                                placeholder={`0 ~ ${feeCap || 0}`}
                              />
                            </Form.Item>
                            <Form.Item
                              label="分账人 C（占订单 %）"
                              name="rent_order_pct_c"
                              rules={[
                                { required: true, message: '请输入分账人 C 占订单比例' },
                                { type: 'number', min: 0, message: '不能小于 0' },
                              ]}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                max={feeCap}
                                step={0.01}
                                precision={4}
                                addonAfter="%"
                                placeholder={`0 ~ ${feeCap || 0}`}
                              />
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      label="公司（占订单 %，自动）"
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.service_fee_rate !== currentValues.service_fee_rate ||
                        prevValues.rent_order_pct_a !== currentValues.rent_order_pct_a ||
                        prevValues.rent_order_pct_b !== currentValues.rent_order_pct_b ||
                        prevValues.rent_order_pct_c !== currentValues.rent_order_pct_c
                      }
                    >
                      {({ getFieldValue }) => {
                        const fee = Number(getFieldValue('service_fee_rate')) || 0;
                        const oa = Number(getFieldValue('rent_order_pct_a')) || 0;
                        const ob = Number(getFieldValue('rent_order_pct_b')) || 0;
                        const oc = Number(getFieldValue('rent_order_pct_c')) || 0;
                        const companyOrder = Math.max(0, fee - oa - ob - oc);
                        return (
                          <InputNumber
                            style={{ width: '100%' }}
                            value={Number(companyOrder.toFixed(4))}
                            disabled
                            addonAfter="%"
                          />
                        );
                      }}
                    </Form.Item>
                  </Col>
                  {/* <Col xs={24} lg={12}>
                    <div style={{ fontWeight: 600, marginBottom: 12 }}>接口存储（占服务费池，只读）</div>
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.service_fee_rate_p3 !== currentValues.service_fee_rate_p3 ||
                        prevValues.service_fee_rate_p4 !== currentValues.service_fee_rate_p4 ||
                        prevValues.service_fee_rate_p5 !== currentValues.service_fee_rate_p5
                      }
                    >
                      {({ getFieldValue }) => {
                        const p3 = getFieldValue('service_fee_rate_p3') || 0;
                        const p4 = getFieldValue('service_fee_rate_p4') || 0;
                        const p5 = getFieldValue('service_fee_rate_p5') || 0;
                        // 展示用：A/B/C 向下截断到 2 位，公司 = 100 − 三项（仅展示；尾差进公司行，避免四舍五入让公司少显示）
                        const aShow = truncateDecimals(p3 * 100, 2);
                        const bShow = truncateDecimals(p4 * 100, 2);
                        const cShow = truncateDecimals(p5 * 100, 2);
                        const companyShow = Number((100 - aShow - bShow - cShow).toFixed(2));
                        const sumShow = aShow + bShow + cShow + companyShow;
                        return (
                          <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            <div>分账人 A：{aShow.toFixed(2)}%</div>
                            <div>分账人 B：{bShow.toFixed(2)}%</div>
                            <div>分账人 C：{cShow.toFixed(2)}%</div>
                            <div>公司：{companyShow.toFixed(2)}%</div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              合计：A+B+C+公司 = {sumShow.toFixed(2)}%（A/B/C 为截断展示，公司为剩余，恒为 100%）
                            </div>
                          </Space>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.service_fee_rate !== currentValues.service_fee_rate ||
                        prevValues.service_fee_rate_p3 !== currentValues.service_fee_rate_p3 ||
                        prevValues.service_fee_rate_p4 !== currentValues.service_fee_rate_p4 ||
                        prevValues.service_fee_rate_p5 !== currentValues.service_fee_rate_p5
                      }
                    >
                      {({ getFieldValue }) => {
                        const feePct = Number(getFieldValue('service_fee_rate')) || 0;
                        const feeDec = feePct / 100;
                        const p3 = getFieldValue('service_fee_rate_p3') || 0;
                        const p4 = getFieldValue('service_fee_rate_p4') || 0;
                        const p5 = getFieldValue('service_fee_rate_p5') || 0;
                        const oaRaw = feeDec > 0 ? p3 * feeDec * 100 : 0;
                        const obRaw = feeDec > 0 ? p4 * feeDec * 100 : 0;
                        const ocRaw = feeDec > 0 ? p5 * feeDec * 100 : 0;
                        const oaShow = truncateDecimals(oaRaw, 4);
                        const obShow = truncateDecimals(obRaw, 4);
                        const ocShow = truncateDecimals(ocRaw, 4);
                        const oCompanyShow = Number((feePct - oaShow - obShow - ocShow).toFixed(4));
                        return (
                          <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                            对应占订单：A {oaShow.toFixed(4)}%，B {obShow.toFixed(4)}%，C {ocShow.toFixed(4)}%，公司 {oCompanyShow.toFixed(4)}%
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Col> */}
                </Row>
              </TabPane>

              <TabPane tab="电表" key="electric">
                <Form.Item
                  label="水电表银行手续费（共用，%）"
                  name="electric_service_fee_rate_p6"
                  rules={[
                    { required: true, message: '请输入银行手续费比例' },
                    { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                  ]}
                  extra={
                    platformBankFeeRate > 0
                      ? `平台默认值：${platformBankFeeRate.toFixed(2)}%（房源配置为0时使用）`
                      : '房源配置为0.0000时将使用平台默认值'
                  }
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    placeholder="请输入银行手续费比例（0-1之间，例如：0.006表示0.6%）"
                    formatter={(value) => {
                      if (!value) return '';
                      return `${(value * 100).toFixed(2)}%`;
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      const num = parseFloat(value.replace('%', ''));
                      return isNaN(num) ? 0 : num / 100;
                    }}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.electric_service_fee_mode !== curr.electric_service_fee_mode}>
                  {({ getFieldValue }) => {
                    const mode = getFieldValue('electric_service_fee_mode');
                    if (mode === 2) return null; // 定额模式时隐藏百分比分账
                    return (
                      <>
                        <div style={{ marginTop: 16, marginBottom: 8, padding: '6px 10px', background: '#e6f7ff', borderRadius: 4, fontSize: 13, color: '#1890ff' }}>
                          电表 · 百分比分账（按电费比例收取时的分账）
                        </div>
                        <Form.Item
                          label="分账人A分账比例（%）"
                          name="electric_service_fee_rate_p3"
                          rules={[
                            { required: true, message: '请输入分账人A分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    placeholder="请输入分账人A分账比例（0-1之间，例如：0.05表示5%）"
                    formatter={(value) => {
                      if (!value) return '';
                      return `${(value * 100).toFixed(2)}%`;
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      const num = parseFloat(value.replace('%', ''));
                      return isNaN(num) ? 0 : num / 100;
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="分账人B分账比例（%）"
                  name="electric_service_fee_rate_p4"
                  rules={[
                    { required: true, message: '请输入分账人B分账比例' },
                    { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    placeholder="请输入分账人B分账比例（0-1之间，例如：0.05表示5%）"
                    formatter={(value) => {
                      if (!value) return '';
                      return `${(value * 100).toFixed(2)}%`;
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      const num = parseFloat(value.replace('%', ''));
                      return isNaN(num) ? 0 : num / 100;
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="分账人C分账比例（%）"
                  name="electric_service_fee_rate_p5"
                  rules={[
                    { required: true, message: '请输入分账人C分账比例' },
                    { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    placeholder="请输入分账人C分账比例（0-1之间，例如：0.05表示5%）"
                    formatter={(value) => {
                      if (!value) return '';
                      return `${(value * 100).toFixed(2)}%`;
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      const num = parseFloat(value.replace('%', ''));
                      return isNaN(num) ? 0 : num / 100;
                    }}
                  />
                </Form.Item>

                <Form.Item
                  label="公司分账比例（%）（自动计算）"
                  shouldUpdate={(prevValues, currentValues) => 
                    prevValues.electric_service_fee_rate_p3 !== currentValues.electric_service_fee_rate_p3 ||
                    prevValues.electric_service_fee_rate_p4 !== currentValues.electric_service_fee_rate_p4 ||
                    prevValues.electric_service_fee_rate_p5 !== currentValues.electric_service_fee_rate_p5
                  }
                >
                  {({ getFieldValue }) => {
                    const p3 = getFieldValue('electric_service_fee_rate_p3') || 0;
                    const p4 = getFieldValue('electric_service_fee_rate_p4') || 0;
                    const p5 = getFieldValue('electric_service_fee_rate_p5') || 0;
                    const companyRate = Math.max(0, 1 - (p3 + p4 + p5));
                    const percentageValue = (companyRate * 100).toFixed(2);
                    return (
                      <InputNumber
                        style={{ width: '100%' }}
                        value={percentageValue}
                        disabled
                        precision={2}
                        formatter={(value) => value ? `${value}%` : ''}
                        parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                      />
                    );
                  }}
                </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.electric_service_fee_mode !== curr.electric_service_fee_mode}>
                  {({ getFieldValue }) => {
                    const mode = getFieldValue('electric_service_fee_mode');
                    if (mode !== 2) return null; // 百分比模式时隐藏定额分账
                    return (
                      <>
                        <div style={{ marginTop: 16, marginBottom: 8, padding: '6px 10px', background: '#f6ffed', borderRadius: 4, fontSize: 13, color: '#52c41a' }}>
                          电表 · 公式分账（按元/度收取时的分账）
                        </div>
                        <Form.Item
                          label="分账人A分账比例（%）"
                          name="electric_service_fee_fixed_p3"
                          rules={[
                            { required: true, message: '请输入分账人A分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人A分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人B分账比例（%）"
                          name="electric_service_fee_fixed_p4"
                          rules={[
                            { required: true, message: '请输入分账人B分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人B分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人C分账比例（%）"
                          name="electric_service_fee_fixed_p5"
                          rules={[
                            { required: true, message: '请输入分账人C分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人C分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="公司分账比例（%）（自动计算）"
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.electric_service_fee_fixed_p3 !== currentValues.electric_service_fee_fixed_p3 ||
                            prevValues.electric_service_fee_fixed_p4 !== currentValues.electric_service_fee_fixed_p4 ||
                            prevValues.electric_service_fee_fixed_p5 !== currentValues.electric_service_fee_fixed_p5
                          }
                        >
                          {({ getFieldValue }) => {
                            const p3 = getFieldValue('electric_service_fee_fixed_p3') || 0;
                            const p4 = getFieldValue('electric_service_fee_fixed_p4') || 0;
                            const p5 = getFieldValue('electric_service_fee_fixed_p5') || 0;
                            const companyRate = Math.max(0, 1 - (p3 + p4 + p5));
                            const percentageValue = (companyRate * 100).toFixed(2);
                            return (
                              <InputNumber
                                style={{ width: '100%' }}
                                value={percentageValue}
                                disabled
                                precision={2}
                                formatter={(value) => value ? `${value}%` : ''}
                                parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                              />
                            );
                          }}
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
              </TabPane>

              <TabPane tab="水表" key="water">
                <Form.Item
                  label="水电表银行手续费（共用，%）"
                  name="water_service_fee_rate_p6"
                  rules={[
                    { required: true, message: '请输入银行手续费比例' },
                    { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                  ]}
                  extra={
                    platformBankFeeRate > 0
                      ? `平台默认值：${platformBankFeeRate.toFixed(2)}%（房源配置为0时使用）`
                      : '房源配置为0.0000时将使用平台默认值'
                  }
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    max={1}
                    step={0.0001}
                    precision={4}
                    placeholder="请输入银行手续费比例（0-1之间，例如：0.006表示0.6%）"
                    formatter={(value) => {
                      if (!value) return '';
                      return `${(value * 100).toFixed(2)}%`;
                    }}
                    parser={(value) => {
                      if (!value) return '';
                      const num = parseFloat(value.replace('%', ''));
                      return isNaN(num) ? 0 : num / 100;
                    }}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.water_service_fee_mode !== curr.water_service_fee_mode}>
                  {({ getFieldValue }) => {
                    const mode = getFieldValue('water_service_fee_mode');
                    if (mode === 2) return null; // 定额模式时隐藏百分比分账
                    return (
                      <>
                        <div style={{ marginTop: 16, marginBottom: 8, padding: '6px 10px', background: '#e6f7ff', borderRadius: 4, fontSize: 13, color: '#1890ff' }}>
                          水表 · 百分比分账（按水费比例收取时的分账）
                        </div>
                        <Form.Item
                          label="分账人A分账比例（%）"
                          name="water_service_fee_rate_p3"
                          rules={[
                            { required: true, message: '请输入分账人A分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人A分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人B分账比例（%）"
                          name="water_service_fee_rate_p4"
                          rules={[
                            { required: true, message: '请输入分账人B分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人B分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人C分账比例（%）"
                          name="water_service_fee_rate_p5"
                          rules={[
                            { required: true, message: '请输入分账人C分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人C分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="公司分账比例（%）（自动计算）"
                          shouldUpdate={(prevValues, currentValues) => 
                            prevValues.water_service_fee_rate_p3 !== currentValues.water_service_fee_rate_p3 ||
                            prevValues.water_service_fee_rate_p4 !== currentValues.water_service_fee_rate_p4 ||
                            prevValues.water_service_fee_rate_p5 !== currentValues.water_service_fee_rate_p5
                          }
                        >
                          {({ getFieldValue }) => {
                            const p3 = getFieldValue('water_service_fee_rate_p3') || 0;
                            const p4 = getFieldValue('water_service_fee_rate_p4') || 0;
                            const p5 = getFieldValue('water_service_fee_rate_p5') || 0;
                            const companyRate = Math.max(0, 1 - (p3 + p4 + p5));
                            const percentageValue = (companyRate * 100).toFixed(2);
                            return (
                              <InputNumber
                                style={{ width: '100%' }}
                                value={percentageValue}
                                disabled
                                precision={2}
                                formatter={(value) => value ? `${value}%` : ''}
                                parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                              />
                            );
                          }}
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.water_service_fee_mode !== curr.water_service_fee_mode}>
                  {({ getFieldValue }) => {
                    const mode = getFieldValue('water_service_fee_mode');
                    if (mode !== 2) return null; // 百分比模式时隐藏定额分账
                    return (
                      <>
                        <div style={{ marginTop: 16, marginBottom: 8, padding: '6px 10px', background: '#f6ffed', borderRadius: 4, fontSize: 13, color: '#52c41a' }}>
                          水表 · 公式分账（按元/吨收取时的分账）
                        </div>
                        <Form.Item
                          label="分账人A分账比例（%）"
                          name="water_service_fee_fixed_p3"
                          rules={[
                            { required: true, message: '请输入分账人A分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人A分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人B分账比例（%）"
                          name="water_service_fee_fixed_p4"
                          rules={[
                            { required: true, message: '请输入分账人B分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人B分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="分账人C分账比例（%）"
                          name="water_service_fee_fixed_p5"
                          rules={[
                            { required: true, message: '请输入分账人C分账比例' },
                            { type: 'number', min: 0, max: 1, message: '比例必须在0-1之间' },
                          ]}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={1}
                            step={0.0001}
                            precision={4}
                            placeholder="请输入分账人C分账比例（0-1之间，例如：0.05表示5%）"
                            formatter={(value) => {
                              if (!value) return '';
                              return `${(value * 100).toFixed(2)}%`;
                            }}
                            parser={(value) => {
                              if (!value) return '';
                              const num = parseFloat(value.replace('%', ''));
                              return isNaN(num) ? 0 : num / 100;
                            }}
                          />
                        </Form.Item>
                        <Form.Item
                          label="公司分账比例（%）（自动计算）"
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.water_service_fee_fixed_p3 !== currentValues.water_service_fee_fixed_p3 ||
                            prevValues.water_service_fee_fixed_p4 !== currentValues.water_service_fee_fixed_p4 ||
                            prevValues.water_service_fee_fixed_p5 !== currentValues.water_service_fee_fixed_p5
                          }
                        >
                          {({ getFieldValue }) => {
                            const p3 = getFieldValue('water_service_fee_fixed_p3') || 0;
                            const p4 = getFieldValue('water_service_fee_fixed_p4') || 0;
                            const p5 = getFieldValue('water_service_fee_fixed_p5') || 0;
                            const companyRate = Math.max(0, 1 - (p3 + p4 + p5));
                            const percentageValue = (companyRate * 100).toFixed(2);
                            return (
                              <InputNumber
                                style={{ width: '100%' }}
                                value={percentageValue}
                                disabled
                                precision={2}
                                formatter={(value) => value ? `${value}%` : ''}
                                parser={(value) => value ? parseFloat(value.replace('%', '')) : 0}
                              />
                            );
                          }}
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
              </TabPane>
            </Tabs>

            <Alert
              message="分账人配置"
              description="请选择分账人A、B、C对应的用户ID。分账人可以是0-3个，不选择则留空。"
              type="info"
              showIcon
              style={{ marginBottom: 16, marginTop: 16 }}
            />

            <Form.Item
              label="分账人A用户ID"
              name="profit_share_p3_id"
            >
              <Select
                style={{ width: '100%' }}
                placeholder="先输入关键词，再在列表中选择（姓名/昵称/手机）"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={false}
                onSearch={searchSplitUsers}
                onOpenChange={(open) => {
                  if (open) clearSplitUserSearchUi();
                }}
                notFoundContent={splitUserSelectNotFound}
              >
                {splitSelectOptionIds(watchedProfitP3).map((id) => {
                  const displayName = buildUserDisplayName(id);
                  return (
                    <Select.Option key={id} value={id} label={displayName}>
                      {displayName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              label="分账人B用户ID"
              name="profit_share_p4_id"
            >
              <Select
                style={{ width: '100%' }}
                placeholder="先输入关键词，再在列表中选择（姓名/昵称/手机）"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={false}
                onSearch={searchSplitUsers}
                onOpenChange={(open) => {
                  if (open) clearSplitUserSearchUi();
                }}
                notFoundContent={splitUserSelectNotFound}
              >
                {splitSelectOptionIds(watchedProfitP4).map((id) => {
                  const displayName = buildUserDisplayName(id);
                  return (
                    <Select.Option key={id} value={id} label={displayName}>
                      {displayName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Form.Item
              label="分账人C用户ID"
              name="profit_share_p5_id"
            >
              <Select
                style={{ width: '100%' }}
                placeholder="先输入关键词，再在列表中选择（姓名/昵称/手机）"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={false}
                onSearch={searchSplitUsers}
                onOpenChange={(open) => {
                  if (open) clearSplitUserSearchUi();
                }}
                notFoundContent={splitUserSelectNotFound}
              >
                {splitSelectOptionIds(watchedProfitP5).map((id) => {
                  const displayName = buildUserDisplayName(id);
                  return (
                    <Select.Option key={id} value={id} label={displayName}>
                      {displayName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Alert
              message="房源指定收账人配置"
              description="请选择房源指定收账人对应的用户ID。收账人用于指定该房源的资金收款人（仅房东使用）。"
              type="info"
              showIcon
              style={{ marginBottom: 16, marginTop: 16 }}
            />

            <Form.Item
              label="房源指定收账人用户ID"
              name="collector_user_id"
            >
              <Select
                style={{ width: '100%' }}
                placeholder="先输入关键词，再在列表中选择（姓名/昵称/手机）"
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={false}
                onSearch={searchSplitUsers}
                onOpenChange={(open) => {
                  if (open) clearSplitUserSearchUi();
                }}
                notFoundContent={splitUserSelectNotFound}
              >
                {splitSelectOptionIds(watchedCollector).map((id) => {
                  const displayName = buildUserDisplayName(id);
                  return (
                    <Select.Option key={id} value={id} label={displayName}>
                      {displayName}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>

            <Alert
              message="售后配置"
              description="请选择充值页售后联系人（仅可从分账人 A/B/C 中选择一个）。如果 A/B/C 都未配置，则无法选择，保存为空。"
              type="info"
              showIcon
              style={{ marginBottom: 16, marginTop: 16 }}
            />

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.profit_share_p3_id !== currentValues.profit_share_p3_id ||
                prevValues.profit_share_p4_id !== currentValues.profit_share_p4_id ||
                prevValues.profit_share_p5_id !== currentValues.profit_share_p5_id ||
                prevValues.after_sales_salesman_user_id !== currentValues.after_sales_salesman_user_id
              }
            >
              {({ getFieldValue }) => {
                const abcIds = [
                  normalizeUserSelectId(getFieldValue('profit_share_p3_id')),
                  normalizeUserSelectId(getFieldValue('profit_share_p4_id')),
                  normalizeUserSelectId(getFieldValue('profit_share_p5_id')),
                ].filter(Boolean);
                const savedAfterId = normalizeUserSelectId(getFieldValue('after_sales_salesman_user_id'));
                const uniqueCandidateIDs = Array.from(new Set(abcIds));
                if (savedAfterId && !uniqueCandidateIDs.includes(savedAfterId)) {
                  uniqueCandidateIDs.push(savedAfterId);
                }
                const optionRows = uniqueCandidateIDs.map((id) => ({
                  id,
                  displayName: buildUserDisplayName(id),
                }));
                const canPick = abcIds.length > 0;
                return (
                  <Form.Item
                    label="售后联系人用户ID"
                    name="after_sales_salesman_user_id"
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder={
                        canPick ? '请选择售后联系人（可选）' : '请先配置分账人A/B/C'
                      }
                      allowClear
                      showSearch
                      disabled={!canPick && !savedAfterId}
                      optionFilterProp="label"
                      filterOption={(input, option) => {
                        const q = String(input ?? '').trim().toLowerCase();
                        if (!q) return false;
                        return String(option?.label ?? '').toLowerCase().includes(q);
                      }}
                    >
                      {optionRows.map(({ id, displayName }) => (
                        <Select.Option key={id} value={id} label={displayName}>
                          {displayName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default SplitConfig;

